// Eval-execution engine D1 — pure logic for the Golden-set Items UI.
//
// DatasetsPages.jsx is a host view-shell (excluded from coverage); every
// piece of testable logic for golden-set authoring lives here so it stays
// 100% Vitest-covered. Mirrors the backend `dataset_import.parse_items`
// semantics (backend is the authoritative validator; this is the preview).

import { COLORS } from "../../../theme/colors";

// Accepted column / key spellings (first match wins, in order) — must match
// backend `dataset_import._INPUT_KEYS` / `_EXPECTED_KEYS`.
const INPUT_KEYS = ["input", "question"];
const EXPECTED_KEYS = ["expected_output", "expected_answer"];

// First present-and-non-null value among `keys`, coerced to string.
function pick(row, keys) {
  for (const k of keys) {
    if (Object.prototype.hasOwnProperty.call(row, k) && row[k] != null) {
      return String(row[k]);
    }
  }
  return "";
}

// ─── CSV ─────────────────────────────────────────────────────────────
//
// Minimal RFC-4180-ish parser: handles quoted fields, embedded commas /
// newlines inside quotes, and "" escapes. Returns an array of string cells
// per record. Trailing blank lines are ignored.
function parseCsvRecords(content) {
  const records = [];
  let field = "";
  let record = [];
  let inQuotes = false;
  let sawAny = false;

  const pushField = () => { record.push(field); field = ""; };
  const pushRecord = () => { records.push(record); record = []; };

  for (let i = 0; i < content.length; i++) {
    const ch = content[i];
    if (inQuotes) {
      if (ch === '"') {
        if (content[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"') { inQuotes = true; sawAny = true; continue; }
    if (ch === ",") { pushField(); sawAny = true; continue; }
    if (ch === "\r") continue;
    if (ch === "\n") {
      pushField();
      // Skip a record that is a single empty cell (blank line).
      if (!(record.length === 1 && record[0] === "")) pushRecord();
      else record = [];
      continue;
    }
    field += ch;
    sawAny = true;
  }
  // Flush trailing field/record (no final newline).
  if (field !== "" || record.length > 0 || (sawAny && records.length === 0)) {
    pushField();
    if (!(record.length === 1 && record[0] === "")) pushRecord();
  }
  return records;
}

function parseCsv(content) {
  const records = parseCsvRecords(content);
  if (records.length === 0) throw new Error("CSV file has no header row");
  const header = records[0].map((h) => (h || "").trim());
  const hasInput = header.some((h) => INPUT_KEYS.includes(h));
  if (!hasInput) {
    throw new Error(
      "CSV missing an input column (expected one of: " + INPUT_KEYS.join(", ") + ")",
    );
  }
  const rows = [];
  for (let r = 1; r < records.length; r++) {
    const cells = records[r];
    const obj = {};
    header.forEach((h, c) => { obj[h] = cells[c] != null ? cells[c] : ""; });
    const input = pick(obj, INPUT_KEYS).trim();
    // DictReader consumes the header, so the first data row is line 2.
    if (!input) throw new Error(`row ${r + 1}: empty input`);
    rows.push({ input, expected_output: pick(obj, EXPECTED_KEYS) });
  }
  return rows;
}

// ─── JSONL ───────────────────────────────────────────────────────────
function parseJsonl(content) {
  const rows = [];
  const lines = content.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const stripped = lines[i].trim();
    if (!stripped) continue;
    let obj;
    try {
      obj = JSON.parse(stripped);
    } catch (e) {
      throw new Error(`row ${i + 1}: invalid JSON (${e.message})`);
    }
    if (obj === null || typeof obj !== "object" || Array.isArray(obj)) {
      throw new Error(`row ${i + 1}: expected a JSON object`);
    }
    const input = pick(obj, INPUT_KEYS).trim();
    if (!input) throw new Error(`row ${i + 1}: missing input`);
    rows.push({ input, expected_output: pick(obj, EXPECTED_KEYS) });
  }
  return rows;
}

// Client-side preview parser. NEVER throws — returns {rows, error} so the
// import modal can render a preview table or a friendly error banner.
//   CSV : header (question|input) + (expected_answer|expected_output)
//   JSONL: one object/line with (input|question) + (expected_output|expected_answer)
// Sniff by filename extension, else by leading '{'. Mirrors backend
// `dataset_import.parse_items`.
export function parseGoldenFile(text, filename) {
  try {
    if (text == null || !String(text).trim()) {
      return { rows: [], error: "empty file" };
    }
    const content = String(text);
    const name = String(filename || "").toLowerCase();
    let rows;
    if (name.endsWith(".jsonl") || name.endsWith(".ndjson")) {
      rows = parseJsonl(content);
    } else if (name.endsWith(".csv")) {
      rows = parseCsv(content);
    } else {
      rows = content.trimStart().startsWith("{") ? parseJsonl(content) : parseCsv(content);
    }
    if (rows.length === 0) return { rows: [], error: "no valid rows found" };
    return { rows, error: null };
  } catch (e) {
    return { rows: [], error: e.message || "could not parse file" };
  }
}

// ─── Draft validation (inline author form) ───────────────────────────
// Input (question) is required + non-blank. Expected answer is optional
// (a trace-less item without ground truth is still valid). Returns an
// error string or null.
export function validateItemDraft(draft) {
  const input = (draft && draft.input != null ? String(draft.input) : "").trim();
  if (!input) return "Question is required";
  return null;
}

// ─── Source provenance badge ─────────────────────────────────────────
// Maps a dataset item's `source` to a display label + COLORS token.
export function sourceBadge(source) {
  switch (source) {
    case "authored":
      return { label: "authored", color: COLORS.textMuted };
    case "imported":
      return { label: "imported", color: COLORS.accentText };
    case "trace":
      return { label: "from trace", color: COLORS.accentText };
    default:
      return { label: source ? String(source) : "—", color: COLORS.textMuted };
  }
}

// ─── Response normalization ──────────────────────────────────────────
// Normalize the /items response into a plain array of item rows. Handles
// {success:false} (→ []), {success, data:[...]}, and a bare array.
export function normalizeItems(resp) {
  if (resp && resp.success === false) return [];
  if (Array.isArray(resp && resp.data)) return resp.data;
  if (Array.isArray(resp)) return resp;
  return [];
}

// ─── Inspector "Add to golden set" payload ───────────────────────────
// Build the args for `observabilityApi.addTraceToDataset` from an
// Inspector turn + the admin-edited expected answer. Returns {payload,
// error}: error is non-null when the target dataset or trace id is
// missing (the Inspector affordance disables Save in that case).
export function buildTraceCapture({ datasetId, traceId, observationId, expectedOutput }) {
  if (!datasetId) return { payload: null, error: "Pick a dataset" };
  if (!traceId) return { payload: null, error: "This turn has no trace to capture" };
  return {
    payload: {
      datasetId,
      traceId,
      observationId: observationId || null,
      expectedOutput: expectedOutput != null ? String(expectedOutput) : "",
    },
    error: null,
  };
}
